import { API_ROUTES, useApiQuery } from '@shared/api-utils';
import { twMerge } from '@shared/themes';
import { useTranslations } from 'next-intl';
import type { ComponentProps } from 'react';
import { useResultsInteraction } from '../../lotteries/ResultsSection/context/ResultsInteractionContext';
import { LotteryResult } from '../../lottery-results/components/LotteryResult/LotteryResult';
import type { Drawing } from '../../lottery-results/types';
import type { ExtraGameName, Line, LotteryName, Participation } from '../types';
import { LineExtraInfo } from './components/LineExtraInfo/LineExtraInfo';
import { ParticipationExtraInfo } from './components/ParticipationExtraInfo/ParticipationExtraInfo';
import { getLineMatching } from './utils';

type YourNumbersProps = ComponentProps<'div'> & {
  participation: Participation;
  lineInfo: Partial<Record<LotteryName, Line[]>>;
  lotteryName: LotteryName;
  options?: string[];
};

/**
 * A component that renders the your numbers section of a lottery ticket.
 * It displays the lines of numbers of the participation and their matching results.
 *
 * @param {Object} props - The component props
 * @param {string} props.className - The class name to apply to the component
 * @param {Record<LotteryName, Line[]>} props.lineInfo - The line information for each lottery
 * @param {Record<LotteryName, WinningInfo[]>} props.lineWinningInfo - The winning information for each lottery
 * @param {LotteryName} props.lotteryName - The name of the lottery
 * @param {string} props.drawingId - The id of the drawing
 * @param {Object} props.props - The rest of the props
 */
function YourNumbers({ participation, lineInfo, lotteryName, options, className, ...props }: YourNumbersProps) {
  const { lineWinningInfo, drawingId, participationState, realWinning } = participation;
  const { selectedOption } = useResultsInteraction();

  const t = useTranslations('lotteries.ticket_details.your_numbers_section');
  const lines = lineInfo[lotteryName] ?? [];

  const { data: results } = useApiQuery<Drawing>(API_ROUTES.RESULTS.BY_LOTTERY_AND_DRAWING.replace(':lotteryName', lotteryName).replace(':drawingId', drawingId), {
    queryOptions: { placeholderData: previousData => previousData },
  });

  const selectedGameName = (selectedOption?.value as LotteryName | ExtraGameName) ?? lotteryName;

  return (
    <div className={twMerge('flex flex-col gap-5', className)} {...props}>
      <div className="flex items-center gap-2 justify-between">
        <h2 className="paragraph-l-strong">{t('title')}</h2>
        <p className="paragraph-s-strong">{t('lines', { count: lines.length })}</p>
      </div>

      <ParticipationExtraInfo lotteryName={lotteryName} participation={participation} options={options} className="w-fit" />

      <div className="flex flex-col gap-3">
        {lines.map(({ numbers, extraNumbers, lineNumber }) => {
          const line = { numbers: numbers.map(Number), extraNumbers: extraNumbers?.map(Number) ?? [], lineNumber };
          const matching = results
            ? getLineMatching(lotteryName, selectedGameName, results, line, lineWinningInfo?.[selectedGameName] ?? [], participationState, realWinning)
            : undefined;

          return (
            <div key={lineNumber} className="flex flex-col gap-3">
              <LotteryResult result={{ numbers: line.numbers, extraNumbers: line.extraNumbers }} lotteryName={lotteryName} matching={matching} />
              <LineExtraInfo lotteryName={lotteryName} participation={participation} lineNumber={lineNumber} className="w-fit mb-1.5" />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export { YourNumbers };
